import { useRegistration } from "@/context/RegistrationContext";
import { DOCUMENT_TYPES, EDUCATION_BOARDS, TC_MANDATORY_BOARDS } from "@/lib/constants";
import { UploadCard } from "@/components/UploadCard";
import { ArrowLeft, ArrowRight, Info, ShieldCheck, GitBranch, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const promptSelectBoard = () => toast.error("Please select your qualifying board (12th standard) first");

const ALT_GROUP_LABELS = {
  twelfth_group: "12th Certificate — one of the three below is required",
  tc_group: "Transfer Certificate — one of the two below is required",
};

const ALT_GROUP_HINTS = {
  twelfth_group: "Upload 12th certificate. If not yet issued, upload 12th Short Memo. If neither is available, upload NOC.",
  tc_group: "Upload TC. If TC is not available, upload NOC in lieu.",
};

export default function DocumentsStep({ onNext, onBack }) {
  const { state, setDocuments, updateCandidate } = useRegistration();

  const isNonIndian =
    state.candidate.nationality && state.candidate.nationality !== "Indian";

  const board = state.candidate.educationBoard || "";
  const tcMandatory = TC_MANDATORY_BOARDS.includes(board);

  // Adjust required flags dynamically. Nationality proof is required only for
  // non-Indians; Transfer Certificate (or NOC in lieu) is required only for
  // State Board & CBSE/ICSE candidates — re-evaluated on every render, so
  // changing the qualifying board mid-form immediately updates what's
  // mandatory. Migration Certificate is never mandatory.
  const docs = DOCUMENT_TYPES.map((d) => {
    if (d.id === "nationality") return { ...d, required: isNonIndian };
    return d;
  });

  const singleRequired = docs.filter((d) => d.required && !d.altGroup);
  const allAltGroupIds = Array.from(new Set(docs.filter((d) => d.altGroup).map((d) => d.altGroup)));
  // tc_group's requiredness follows the qualifying board; other alt groups
  // (currently just twelfth_group) are always required. The group still
  // renders either way — this only controls whether it blocks "Continue".
  const requiredAltGroupIds = allAltGroupIds.filter((g) => g !== "tc_group" || tcMandatory);

  const uploadedSingle = singleRequired.filter(
    (d) => (state.documents[d.id] || []).length > 0
  ).length;
  const totalSingle = singleRequired.length;

  const altGroupsSatisfied = requiredAltGroupIds.filter((g) =>
    docs
      .filter((d) => d.altGroup === g)
      .some((d) => (state.documents[d.id] || []).length > 0)
  ).length;
  const totalAltGroups = requiredAltGroupIds.length;

  const canNext =
    !!board && uploadedSingle === totalSingle && altGroupsSatisfied === totalAltGroups;

  // 10th Standard Certificate leads the page — pulled out and rendered before
  // everything else (including the alt-groups), rather than wherever it falls
  // in the default document order.
  const tenthDoc = docs.find((d) => d.id === "tenth");
  const otherDocs = docs.filter((d) => !d.altGroup && d.id !== "tenth");
  const groupedDocs = allAltGroupIds.map((g) => ({
    id: g,
    label: ALT_GROUP_LABELS[g] || g,
    required: requiredAltGroupIds.includes(g),
    docs: docs.filter((d) => d.altGroup === g),
    satisfied: docs
      .filter((d) => d.altGroup === g)
      .some((d) => (state.documents[d.id] || []).length > 0),
  }));

  return (
    <div className="space-y-6">
      <div className="mu-card p-5 bg-blue-50/50 border-blue-100" data-testid="education-board-card">
        <div className="flex items-start gap-3">
          <GraduationCap className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#111]">Which board did you complete your 12th standard from?<span className="text-[#B60F2A] font-bold">*</span></div>
            <p className="text-xs text-[#6B7280] mt-1">Select a board to unlock document upload below.</p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="education-board-selector">
              {EDUCATION_BOARDS.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  data-testid={`board-option-${b.id}`}
                  onClick={() => updateCandidate({ educationBoard: b.id })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    board === b.id
                      ? "bg-[#B60F2A] text-white border-[#B60F2A]"
                      : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#B60F2A]"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mu-card p-5 bg-blue-50/50 border-blue-100" data-testid="hyderabad-card">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#111]">Are you from Hyderabad?</div>
            <div className="mt-3 flex gap-2" data-testid="hyderabad-selector">
              {[{ value: true, label: "Yes" }, { value: false, label: "No" }].map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  data-testid={`hyderabad-option-${opt.value}`}
                  onClick={() => updateCandidate({ isFromHyderabad: opt.value })}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium border transition ${
                    state.candidate.isFromHyderabad === opt.value
                      ? "bg-[#B60F2A] text-white border-[#B60F2A]"
                      : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#B60F2A]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* "Hosteller or Day Scholar?" question removed — Finance now shows all
          fee checkboxes for every candidate instead of gating on residency,
          and hostel-copy printing keys off the real hostel allotment
          (hostel_student_profiles), not this self-declared answer. */}

      <div className="mu-card p-5 bg-[#FBE7EA]/40 border-[#FBE7EA]">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-[#111] leading-relaxed">
              Upload a <span className="font-semibold">clear scanned image or a document downloaded from DigiLocker</span>. An asterisk (<span className="text-[#B60F2A] font-bold">*</span>) denotes a mandatory document. Each file must be <span className="font-semibold">less than 1 MB</span> in size.
            </p>
            <div className="mt-3 text-sm text-[#111]">
              <span className="font-semibold">{uploadedSingle}</span> of <span className="font-semibold">{totalSingle}</span> single required · <span className="font-semibold">{altGroupsSatisfied}</span> of <span className="font-semibold">{totalAltGroups}</span> either/or groups satisfied.
              {!isNonIndian && (
                <span className="text-xs text-[#6B7280] block mt-0.5">
                  Nationality proof is only mandatory for non-Indian candidates.
                </span>
              )}
            </div>
            <div className="mt-3 h-2 rounded-full bg-white overflow-hidden">
              <div
                className="h-full bg-[#B60F2A] transition-all duration-500"
                style={{ width: `${((uploadedSingle + altGroupsSatisfied) / Math.max(totalSingle + totalAltGroups, 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mu-card p-4 md:p-5 bg-blue-50/50 border-blue-100 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-sm text-[#111]">
          <span className="font-semibold">Tip:</span> Prefer DigiLocker downloads —
          they are pre-verified and instantly accepted by the university.
        </div>
      </div>

      {/* 10th Standard Certificate — always shown first */}
      {tenthDoc && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <UploadCard
            doc={tenthDoc}
            files={state.documents[tenthDoc.id] || []}
            onChange={(next) => setDocuments(tenthDoc.id, next)}
            locked={!board}
            onLockedClick={promptSelectBoard}
          />
        </div>
      )}

      {/* Alternative document groups */}
      {groupedDocs.map((g) => (
        <div
          key={g.id}
          className={`rounded-2xl border-2 border-dashed p-4 md:p-5 transition ${
            g.satisfied
              ? "border-green-200 bg-green-50/40"
              : g.required
              ? "border-[#FBE7EA] bg-[#FBE7EA]/20"
              : "border-[#E5E7EB] bg-[#F7F7F7]/40"
          }`}
          data-testid={`alt-group-${g.id}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <GitBranch className={`h-4 w-4 ${g.satisfied ? "text-green-700" : g.required ? "text-[#B60F2A]" : "text-[#9CA3AF]"}`} />
              <div>
                <div className={`text-sm font-semibold ${g.satisfied ? "text-green-800" : "text-[#111]"}`}>
                  {g.label}
                </div>
                <div className="text-xs text-[#6B7280] mt-0.5">
                  {ALT_GROUP_HINTS[g.id] || "Please upload whichever is applicable."}
                </div>
              </div>
            </div>
            <span
              className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-full ${
                g.satisfied
                  ? "bg-green-100 text-green-700"
                  : g.required
                  ? "bg-white text-[#B60F2A] border border-[#FBE7EA]"
                  : "bg-white text-[#6B7280] border border-[#E5E7EB]"
              }`}
            >
              {g.satisfied ? "Satisfied" : g.required ? "One of these required" : "Optional"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {g.docs.map((d) => (
              <UploadCard
                key={d.id}
                doc={{ ...d, required: false }}
                files={state.documents[d.id] || []}
                onChange={(next) => setDocuments(d.id, next)}
                locked={!board}
                onLockedClick={promptSelectBoard}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Remaining docs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {otherDocs.map((d) => (
          <UploadCard
            key={d.id}
            doc={d}
            files={state.documents[d.id] || []}
            onChange={(next) => setDocuments(d.id, next)}
            locked={!board}
            onLockedClick={promptSelectBoard}
          />
        ))}
      </div>

      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="mu-btn-secondary" data-testid="btn-back-documents">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={() => {
            if (!board) {
              promptSelectBoard();
              return;
            }
            if (!canNext) {
              toast.error("Please upload all mandatory (*) documents and satisfy either/or groups to continue");
              return;
            }
            onNext();
          }}
          className="mu-btn-primary"
          data-testid="btn-next-documents"
          disabled={!canNext}
        >
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
